import { z } from 'zod';
import { httpsCallable, type Functions } from 'firebase/functions';
import { createBackendGatewayError, isBackendGatewayError } from '../../utils/backendGatewayError';
import type { ReportDetail, ReportReviewDecision } from './report';
import { adaptBackofficeReportDetail } from './reportProjectionAdapter';
import { isAuthoritativeReportAbsenceError } from './reportAbsenceError';
import {
  type BackofficeCanonicalReportFilterItem,
  type BackofficeListReportsParams,
  type BackofficeMapReportsParams,
  type BackofficeReportProjection,
  type BackofficeReportSort,
  type PaginatedBackofficeReports,
  type PaginatedTableReports,
  type ReportMapPoint,
  type ReportExportResult,
  type ReportPhotoAccessUrls,
  type ReportsBackendGateway,
} from './reportsGateway';
import {
  assertReportListQueryCapabilities,
  toCanonicalReportFilters,
} from './reportQueryCapabilities';

const REPORT_BACKEND_OPERATION = 'report';
const REPORT_LIST_OPERATION = 'reports';
const REPORT_EXPORT_OPERATION = 'export';

const OptionalStringFieldSchema = z
  .string()
  .nullish()
  .transform((value) => value ?? undefined);

const ReportRetentionHoldSchema = z
  .object({
    active: z.boolean(),
    reason: z.string().optional(),
    holdUntilMs: z.number().int().nonnegative().optional(),
  })
  .strict();

interface BackofficeReportWireFilterItem {
  field: BackofficeCanonicalReportFilterItem['field'];
  operator: BackofficeCanonicalReportFilterItem['op'];
  value: BackofficeCanonicalReportFilterItem['value'];
}

const toBackofficeReportWireFilters = (
  filters: BackofficeCanonicalReportFilterItem[]
): BackofficeReportWireFilterItem[] =>
  filters.map(({ field, op, value }) => ({
    field,
    operator: op,
    value,
  }));

const GetReportPhotoAccessUrlsResponseSchema = z.object({
  reportId: z.string(),
  items: z.array(
    z.object({
      mediaIdentity: OptionalStringFieldSchema,
      storagePath: z.string(),
      url: z.string(),
    })
  ),
});

const ExportReportsResponseSchema = z.object({
  kind: z.literal('download'),
  downloadUrl: z.string().url(),
  fileName: z.string(),
  contentType: z.string(),
});

const EmptyExportReportsResponseSchema = z.object({
  kind: z.literal('empty'),
  message: z.string(),
});

const ExportReportsGatewayResponseSchema = z.union([
  ExportReportsResponseSchema,
  EmptyExportReportsResponseSchema,
]);

const DEFAULT_REPORT_EXPORT_SORT = {
  field: 'sightingDate' as const,
  direction: 'desc' as const,
};

const BackofficeReportProjectionSchema = z.object({
  id: z.string(),
  createdAtMs: z.number(),
  sightingDate: z.string(),
  habitatType: z.string(),
  lat: z.number(),
  lng: z.number(),
  observationType: z.string(),
  otherHabitat: OptionalStringFieldSchema,
  photos: z.array(z.string()),
  reviewStatus: z.enum(['awaitingReview', 'confirmed', 'rejected']),
  confirmationFinalityAtMs: z.number().int().nonnegative().optional(),
  rejectionFinalityAtMs: z.number().int().nonnegative().optional(),
  retentionHold: ReportRetentionHoldSchema.optional(),
  userId: z.string(),
  species: z.string(),
  faceScreeningPendingCount: z.number().int().nonnegative(),
});

const PaginatedBackofficeReportProjectionSchema = z.object({
  items: z.array(BackofficeReportProjectionSchema),
  page: z.number(),
  pageSize: z.number(),
  totalCount: z.number(),
});

const ReportMapPointSchema = z.object({
  id: z.string(),
  lat: z.number(),
  lng: z.number(),
  reviewStatus: z.enum(['awaitingReview', 'confirmed', 'rejected']),
  species: z.string(),
  sightingDate: z.string(),
});

const ReportMapPointsResponseSchema = z.object({
  points: z.array(ReportMapPointSchema),
});

const GetReportDetailByIdResponseSchema = z.object({
  report: BackofficeReportProjectionSchema,
  reviewMetadata: z.object({
    kind: z.literal('report-review-metadata').optional(),
    labels: z.array(z.string()).optional(),
  }),
  faceScreeningReviewSignals: z
    .array(
      z.object({
        mediaIdentity: OptionalStringFieldSchema,
        kind: z.literal('face-screening-review-signal').optional(),
        mediaStoragePath: z.string(),
        warningKind: z.enum(['face_detected', 'screening_failed']),
        overrideAccepted: z.boolean(),
        overrideAcceptedAtMs: z.number(),
        screenedAtMs: z.number(),
        detectorImplementation: z.string(),
        detectorModelVersion: z.string().optional(),
        reviewerSummary: z.string(),
        detectorDetailSummary: z.string(),
      })
    )
    .optional(),
  faceScreeningFalsePositiveHistory: z
    .array(
      z.object({
        mediaIdentity: OptionalStringFieldSchema,
        kind: z.literal('face-screening-false-positive').optional(),
        mediaStoragePath: z.string(),
        warningKind: z.enum(['face_detected', 'screening_failed']),
        overrideAccepted: z.boolean(),
        overrideAcceptedAtMs: z.number(),
        screenedAtMs: z.number(),
        detectorImplementation: z.string(),
        detectorModelVersion: z.string().optional(),
        reviewerSummary: z.string(),
        detectorDetailSummary: z.string(),
        classifiedAtMs: z.number(),
        classifiedByUserId: z.string(),
      })
    )
    .optional(),
});

const ReportFaceScreeningReviewActionResponseSchema = z.object({
  success: z.boolean(),
  reportRejected: z.boolean(),
  removedMediaStoragePath: z.string().nullable(),
});

export const createFirebaseReportsBackendGateway = (
  functions: Functions
): ReportsBackendGateway => {
  const listBackofficeReportsCallable = httpsCallable<
    {
      filters: BackofficeReportWireFilterItem[];
      sort?: BackofficeReportSort;
      page: number;
      pageSize: number;
    },
    z.infer<typeof PaginatedBackofficeReportProjectionSchema>
  >(functions, 'listBackofficeReports');

  const listBackofficeReportMapPointsCallable = httpsCallable<
    {
      filters?: BackofficeReportWireFilterItem[];
    },
    z.infer<typeof ReportMapPointsResponseSchema>
  >(functions, 'listBackofficeReportMapPoints');

  const getReportDetailByIdCallable = httpsCallable<
    { id: string },
    z.infer<typeof GetReportDetailByIdResponseSchema>
  >(functions, 'getReportDetailById');

  const getReportPhotoAccessUrlsCallable = httpsCallable<
    { id: string },
    z.infer<typeof GetReportPhotoAccessUrlsResponseSchema>
  >(functions, 'getReportPhotoAccessUrls');

  const updateReportStatusCallable = httpsCallable<
    { id: string; reviewStatus: ReportReviewDecision; finalizeRejection?: true },
    unknown
  >(functions, 'updateReportStatus', { limitedUseAppCheckTokens: true });
  const updateReportReviewMetadataCallable = httpsCallable<
    { id: string; reviewMetadata: { labels?: string[] } },
    unknown
  >(functions, 'updateReportReviewMetadata');
  const updateReportSpeciesCallable = httpsCallable<{ id: string; species: string }, unknown>(
    functions,
    'updateReportSpecies'
  );
  const classifyReportFaceScreeningFalsePositiveCallable = httpsCallable<
    { id: string; mediaStoragePath: string; mediaIdentity?: string },
    z.infer<typeof ReportFaceScreeningReviewActionResponseSchema>
  >(functions, 'classifyReportFaceScreeningFalsePositive');
  const removeReportFaceScreeningMediaCallable = httpsCallable<
    { id: string; mediaStoragePath: string; mediaIdentity?: string },
    z.infer<typeof ReportFaceScreeningReviewActionResponseSchema>
  >(functions, 'removeReportFaceScreeningMedia');
  const exportBackofficeReportsCallable = httpsCallable<
    {
      filters: BackofficeReportWireFilterItem[];
      format?: 'xlsx' | 'csv';
      sort?: BackofficeReportSort;
    },
    z.infer<typeof ExportReportsResponseSchema>
  >(functions, 'exportBackofficeReports');

  const listBackofficeReports = async (
    params: BackofficeListReportsParams
  ): Promise<PaginatedBackofficeReports> => {
    try {
      const request = {
        filters: toBackofficeReportWireFilters(params.filters),
        page: Math.max(1, Math.floor(params.page) + 1),
        pageSize: normalizePage(params.pageSize),
        sort: params.sort ?? {
          field: 'sightingDate' as const,
          direction: 'desc' as const,
        },
      };

      const response = await listBackofficeReportsCallable(request);
      const parsedResponse = PaginatedBackofficeReportProjectionSchema.parse(response.data);

      return {
        items: parsedResponse.items.map((item) => ({
          id: item.id,
          createdAt: item.createdAtMs,
          sightingDate: item.sightingDate,
          habitatType: item.habitatType,
          lat: item.lat,
          lng: item.lng,
          observationType: item.observationType,
          otherHabitat: item.otherHabitat,
          photos: item.photos,
          reviewStatus: item.reviewStatus,
          userId: item.userId,
          species: item.species,
          faceScreeningPendingCount: item.faceScreeningPendingCount,
        })),
        page: parsedResponse.page,
        pageSize: parsedResponse.pageSize,
        totalCount: parsedResponse.totalCount,
      };
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw createBackendGatewayError(REPORT_LIST_OPERATION, 'response-validation', error);
      }

      throw createBackendGatewayError(REPORT_LIST_OPERATION, 'fetch-failed', error);
    }
  };

  return {
    listBackofficeReports,

    async getReportsForTable(params: BackofficeListReportsParams): Promise<PaginatedTableReports> {
      const result = await listBackofficeReports(params);

      return {
        items: result.items,
        page: result.page,
        pageSize: result.pageSize,
        totalCount: result.totalCount,
      };
    },

    async getReportsForMap(params: BackofficeMapReportsParams): Promise<ReportMapPoint[]> {
      try {
        const response = await listBackofficeReportMapPointsCallable({
          filters: toBackofficeReportWireFilters(params.filters),
        });
        const parsed = ReportMapPointsResponseSchema.parse(response.data);

        return parsed.points;
      } catch (error) {
        if (error instanceof z.ZodError) {
          throw createBackendGatewayError(REPORT_LIST_OPERATION, 'response-validation', error);
        }

        throw toMapBackendGatewayError(error);
      }
    },

    async getReportDetailById(id: string): Promise<ReportDetail> {
      try {
        const response = await getReportDetailByIdCallable({ id });
        const parsed = GetReportDetailByIdResponseSchema.parse(response.data);
        return adaptBackofficeReportDetail(parsed);
      } catch (error) {
        if (error instanceof z.ZodError) {
          throw createBackendGatewayError(REPORT_BACKEND_OPERATION, 'response-validation', error);
        }

        if (isAuthoritativeReportAbsenceError(error)) {
          throw createBackendGatewayError(REPORT_BACKEND_OPERATION, 'report-absent', error);
        }

        throw createBackendGatewayError(REPORT_BACKEND_OPERATION, 'fetch-failed', error);
      }
    },

    async getReportPhotoAccessUrls(id: string): Promise<ReportPhotoAccessUrls> {
      try {
        const response = await getReportPhotoAccessUrlsCallable({ id });
        const parsed = GetReportPhotoAccessUrlsResponseSchema.parse(response.data);
        return parsed;
      } catch (error) {
        if (error instanceof z.ZodError) {
          throw createBackendGatewayError(REPORT_BACKEND_OPERATION, 'response-validation', error);
        }

        throw createBackendGatewayError(REPORT_BACKEND_OPERATION, 'photo-access-failed', error);
      }
    },

    async updateReportStatus(id: string, status: ReportReviewDecision): Promise<void> {
      try {
        await updateReportStatusCallable({ id, reviewStatus: status });
      } catch (error) {
        throw createBackendGatewayError(REPORT_BACKEND_OPERATION, 'update-failed', error);
      }
    },

    async finalizeReportRejection(id: string): Promise<void> {
      try {
        await updateReportStatusCallable({
          id,
          reviewStatus: 'rejected',
          finalizeRejection: true,
        });
      } catch (error) {
        throw createBackendGatewayError(REPORT_BACKEND_OPERATION, 'update-failed', error);
      }
    },

    async updateReportReviewMetadata(
      id: string,
      reviewMetadata: { labels?: string[] }
    ): Promise<void> {
      try {
        await updateReportReviewMetadataCallable({ id, reviewMetadata });
      } catch (error) {
        throw createBackendGatewayError(REPORT_BACKEND_OPERATION, 'update-failed', error);
      }
    },

    async updateReportSpecies(id: string, species: string): Promise<void> {
      try {
        await updateReportSpeciesCallable({ id, species });
      } catch (error) {
        throw createBackendGatewayError(REPORT_BACKEND_OPERATION, 'update-failed', error);
      }
    },

    async classifyReportFaceScreeningFalsePositive(
      id: string,
      mediaStoragePath: string,
      mediaIdentity?: string
    ) {
      try {
        const response = await classifyReportFaceScreeningFalsePositiveCallable({
          id,
          mediaStoragePath,
          mediaIdentity,
        });
        return ReportFaceScreeningReviewActionResponseSchema.parse(response.data);
      } catch (error) {
        if (error instanceof z.ZodError) {
          throw createBackendGatewayError(REPORT_BACKEND_OPERATION, 'response-validation', error);
        }

        throw createBackendGatewayError(REPORT_BACKEND_OPERATION, 'update-failed', error);
      }
    },

    async removeReportFaceScreeningMedia(
      id: string,
      mediaStoragePath: string,
      mediaIdentity?: string
    ) {
      try {
        const response = await removeReportFaceScreeningMediaCallable({
          id,
          mediaStoragePath,
          mediaIdentity,
        });
        return ReportFaceScreeningReviewActionResponseSchema.parse(response.data);
      } catch (error) {
        if (error instanceof z.ZodError) {
          throw createBackendGatewayError(REPORT_BACKEND_OPERATION, 'response-validation', error);
        }

        throw createBackendGatewayError(REPORT_BACKEND_OPERATION, 'update-failed', error);
      }
    },

    async exportBackofficeReports(
      filters: BackofficeCanonicalReportFilterItem[],
      sort: BackofficeReportSort | undefined,
      format: 'xlsx' | 'csv' = 'xlsx'
    ): Promise<ReportExportResult> {
      try {
        const normalizedExport = normalizeExportFiltersAndSort(filters, sort);

        const response = await exportBackofficeReportsCallable({
          filters: normalizedExport.filters,
          format,
          sort: normalizedExport.sort,
        });

        const parsedResponse = ExportReportsGatewayResponseSchema.parse(response.data);
        return parsedResponse;
      } catch (error) {
        if (error instanceof z.ZodError) {
          throw createBackendGatewayError(REPORT_EXPORT_OPERATION, 'response-validation', error);
        }

        throw createBackendGatewayError(REPORT_EXPORT_OPERATION, 'failed', error);
      }
    },
  };
};

const normalizePage = (pageSize: number): number => {
  if (!Number.isFinite(pageSize) || pageSize <= 0) {
    return 10;
  }

  return Math.min(Math.floor(pageSize), 100);
};

const getStructuredReason = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const record = error as Record<string, unknown>;
  if (typeof record.reason === 'string') {
    return record.reason;
  }

  const detailSources = [
    record.details,
    (record.customData as Record<string, unknown> | undefined)?.details,
  ];

  for (const source of detailSources) {
    if (!Array.isArray(source)) {
      continue;
    }

    for (const detail of source) {
      if (
        detail &&
        typeof detail === 'object' &&
        typeof (detail as Record<string, unknown>).reason === 'string'
      ) {
        return (detail as Record<string, unknown>).reason as string;
      }
    }
  }

  return undefined;
};

const toMapBackendGatewayError = (error: unknown) => {
  if (isBackendGatewayError(error)) {
    return error;
  }

  const structuredReason = getStructuredReason(error);
  if (
    structuredReason === 'report/map-result-too-large' ||
    structuredReason === 'map-result-too-large'
  ) {
    return createBackendGatewayError('report', 'map-result-too-large', error);
  }

  return createBackendGatewayError('report', 'fetch-failed', error);
};

const normalizeExportFiltersAndSort = (
  filters: BackofficeCanonicalReportFilterItem[],
  sort: BackofficeReportSort | undefined
): {
  filters: BackofficeReportWireFilterItem[];
  sort: BackofficeReportSort;
} => {
  const canonicalSort = sort ?? DEFAULT_REPORT_EXPORT_SORT;
  assertReportListQueryCapabilities(filters, canonicalSort);

  return {
    filters: toBackofficeReportWireFilters(toCanonicalReportFilters(filters)),
    sort: canonicalSort,
  };
};

export type {
  BackofficeCanonicalReportFilterItem,
  BackofficeListReportsParams,
  BackofficeMapReportsParams,
  BackofficeReportProjection,
  BackofficeReportSort,
  PaginatedBackofficeReports,
  PaginatedTableReports,
  ReportMapPoint,
  ReportExportResult,
  ReportPhotoAccessUrls,
  ReportsBackendGateway,
};
